import { all, call, fork, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import * as ReportingRead from '../../Apis/ReportingReadApi';
import * as G2SAnalytics from '../../Apis/InsightsReportApi';
import * as Actions from './RecordingsActions';
import { forceLoginIfExpired } from '../Utils/RedirectionUtils';
import { chunkArray, getUnique } from '../../Utils/ArrayUtils';
import { RecordingSource } from '../../Utils/Constants';
import Recording from '../../Model/Recording/Recording';

export function* getRecordingsWatcher() {
  yield takeEvery(Actions.GET_RECORDINGS_REQUEST, getRecordingsWorker);
}

export function* getFilterWatcher() {
  yield takeLatest(Actions.FILTER_RECORDINGS_BY_COUNT_REQUEST, filterWorker);
}

export function getUserMetaKeys(state) {
  let { userKey, accountKey } = state.reduced_user.user;
  return { userKey, accountKey };
}

export function* getRecordingsWorker(action) {
  try {
    const { fromTime, toTime } = action.payload;
    let { data: recordingsData } = yield call(ReportingRead.getRecordings, fromTime, toTime);

    let recordingsList = buildRecordingsList(recordingsData);
    let recordingKeys = getUnique(recordingsList.map(r => r.offlineWebinarKey));
    let g2sRecordingDetails = yield* getG2SRecordingDetails(recordingKeys);
    let finalRecordingsList = addG2SInformation(recordingsList, g2sRecordingDetails);

    let videoCountHackFixRecordingList = fixViewCountHack(finalRecordingsList, recordingsData, g2sRecordingDetails);

    yield put({
      type: Actions.GET_RECORDINGS_SUCCESS,
      payload: { recordingsList: videoCountHackFixRecordingList }
    });
  } catch (error) {
    yield put({ type: Actions.GET_RECORDINGS_FAILURE, error });
    forceLoginIfExpired(error);
  }
}

function* getG2SRecordingDetails(recordingKeys = []) {
  const recordingKeysArray = chunkArray(recordingKeys, 50);
  const { userKey, accountKey } = yield select(getUserMetaKeys);
  let g2sRecordingDetails = [];
  for (let i = 0; i < recordingKeysArray.length; i++) {
    try {
      const { data: g2sDetails } = yield call(
        G2SAnalytics.getG2SRecordings,
        recordingKeysArray[i],
        userKey,
        accountKey
      );
      g2sRecordingDetails.push(...g2sDetails);
    } catch (error) {
      console.error('error occured while fetching g2s details', error);
    }
  }
  return g2sRecordingDetails;
}

function buildRecordingsList(recordingsData = []) {
  let recordingsList = [];
  recordingsData.forEach(recording => {
    const { viewCountStatistics = {}, title, dateUploaded, recordingKey, size } = recording;
    const { g2wViewCount = 0, g2sViewCount = 0, g2sEmbeddedViewCount = 0 } = viewCountStatistics;

    const recordingBuilder = new Recording.Builder(recordingKey)
      .withTitle(title)
      .withOfflineWebinarKey(recordingKey)
      .withDateUploaded(dateUploaded)
      .withShareCount(0)
      .withLength(null)
      .withSize(size)
      .withAvgWatchTime(null);

    const g2wRecording = recordingBuilder
      .withViewCount(g2wViewCount)
      .withSource(RecordingSource.G2W)
      .build();
    recordingsList.push(g2wRecording);

    //g2s recording
    if (g2sViewCount > 0) {
      let g2sRecording = recordingBuilder
        .withViewCount(g2sViewCount)
        .withSource(RecordingSource.G2S)
        .build();
      g2sRecording.recordingKey = recordingKey + '-g2s';
      recordingsList.push(g2sRecording);
    }

    //g2s embedded recording
    if (g2sEmbeddedViewCount > 0) {
      const g2sEmbedded = recordingBuilder
        .withViewCount(g2sEmbeddedViewCount)
        .withSource(RecordingSource.G2S_EMBEDDED)
        .build();
      g2sEmbedded.recordingKey = recordingKey + '-g2s-embedded';
      recordingsList.push(g2sEmbedded);
    }
  });
  return recordingsList;
}

function addG2SInformation(recordingsList = [], g2sRecordingDetails = []) {
  let missingG2SRecordings = [];
  g2sRecordingDetails.forEach(g2sRecording => {
    const { prodRefKey, title, publishedDate, totalViews, totalShares, videoLength, avgWatchTime } = g2sRecording;
    const index = recordingsList.findIndex(
      rec => rec.offlineWebinarKey === prodRefKey && rec.source === RecordingSource.G2S
    );

    if (index !== -1) {
      let rec = recordingsList[index];
      rec.title = title;
      rec.dateUploaded = publishedDate;
      rec.viewCount = totalViews;
      rec.shareCount = totalShares;
      rec.length = videoLength;
      rec.avgWatchTime = avgWatchTime;
    } else {
      const missingG2SRecording = new Recording.Builder(prodRefKey + '-g2s')
        .withOfflineWebinarKey(prodRefKey)
        .withTitle(title)
        .withDateUploaded(publishedDate)
        .withShareCount(totalShares)
        .withLength(videoLength)
        .withSize(null)
        .withAvgWatchTime(avgWatchTime)
        .withViewCount(totalViews)
        .withSource(RecordingSource.G2S)
        .build();
      missingG2SRecordings.push(missingG2SRecording);
    }
  });
  recordingsList.push(...missingG2SRecordings);
  return recordingsList;
}

/**
 * HACK: we are calculating view as follows explanation can be found here https://confluence.ops.expertcity.com/display/collaboration/G2W+Analytics+SPA+G2S+views+hack
 * @param finalRecordingsList
 * @param recordingsData
 * @param g2sRecordingDetails
 * @returns {Array}
 */
function fixViewCountHack(finalRecordingsList = [], recordingsData = [], g2sRecordingDetails = []) {
  finalRecordingsList.forEach((recording, index, array) => {
    const recordingInfoFromReporting =
      recordingsData.filter(rec => rec.recordingKey === recording.offlineWebinarKey)[0] || {};
    const { viewCountStatistics = {}, viewCount: totalViewCount = 0 } = recordingInfoFromReporting;
    const { g2sEmbeddedViewCount = 0 } = viewCountStatistics;

    const recordingInfoFromInsightReportService =
      g2sRecordingDetails.filter(rec => rec.prodRefKey === recording.offlineWebinarKey)[0] || {};
    const { totalViews: totalG2SViewsInsightReport = 0 } = recordingInfoFromInsightReportService;

    switch (recording.source) {
      case RecordingSource.G2W:
        array[index].viewCount =
          totalViewCount - g2sEmbeddedViewCount - (totalG2SViewsInsightReport - g2sEmbeddedViewCount);
        break;
      case RecordingSource.G2S:
        array[index].viewCount = totalG2SViewsInsightReport - g2sEmbeddedViewCount;
        break;
      case RecordingSource.G2S_EMBEDDED:
        return;
      default:
        console.warn('invalide recording source');
        return;
    }
  });

  return finalRecordingsList;
}

function delay(duration) {
  return new Promise(resolve => {
    setTimeout(() => resolve(true), duration);
  });
}

export function* filterWorker(action) {
  // maybe reduce this delay
  yield call(delay, 50);
  yield put({
    type: Actions.FILTER_RECORDINGS_BY_COUNT,
    payload: action.payload
  });
}

export default function* rootSaga() {
  yield all([fork(getRecordingsWatcher), fork(getFilterWatcher)]);
}



// WEBPACK FOOTER //
// ./src/Reducks/Recordings/RecordingsSagas.js